import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  downloadAccountDocument,
  listAccountBonds,
  listAccountDocuments,
  type AccountBond,
  type AccountDocument,
} from "@/lib/brokerAccounts";
import {
  listBondSubmissions,
  type BondSubmissionListItem,
} from "@/lib/brokerBonds";
import { BondOrdersTable } from "@/components/BondOrdersTable";
import { IssuedBondsTable } from "@/components/IssuedBondsTable";
import { formatListDate } from "@/lib/listFormat";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";

// BRK-S139 — the account profile's tab row.
//
// Three tabs carry real content and three are HONEST EMPTY STATES. That is the
// whole design decision here, and it is a decision rather than a shortcut:
// Signal's wireframe shows seven tabs, and the parked three (Quotes, Invoices,
// Renewals) have no broker-facing content yet. The options were to hide them, to
// fake them, or to say so.
//
// **Bonds became real in BRK-S175** — the one parked tab whose content existed
// upstream (PMS-S463 publishes the insured's completed issuances). It is the
// intended shape of this row over time: a tab stops saying "coming soon" when
// there is something true to put in it, and never before.
//
// Hiding them would make the profile look finished and then GROW tabs a broker
// has to rediscover. Faking them — a count, a placeholder row, a "3 open" chip —
// is worse than either: a number on a bond portal reads as a fact about the
// insured's money. So they render, they are reachable, and each says "coming
// soon" and nothing else. No counts on the tab labels for the same reason: a
// count is content.
//
// NOTHING IS PROMISED BY NAME. Outstanding capacity, the Good Guy Letter and the
// GIA are parked too, and they are deliberately ABSENT rather than listed as
// coming — a named placeholder is a commitment nobody made.

/** The tabs, in Signal's own order. `ready` marks the ones with content. */
const TABS = [
  { id: "submissions", label: "Submissions", ready: true },
  { id: "documents", label: "Documents", ready: true },
  { id: "bonds", label: "Bonds", ready: true },
  { id: "quotes", label: "Quotes", ready: false },
  { id: "invoices", label: "Invoices", ready: false },
  { id: "renewals", label: "Renewals", ready: false },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function SuretyAccountTabs({ accountId }: { accountId: string }) {
  const [active, setActive] = useState<TabId>("submissions");

  return (
    <section className="flex flex-col gap-4">
      {/* Scrolls rather than wraps below `sm`: six tabs stacked into three rows
          push the panel off the first screen at 375px. The scroller is exactly
          the page's width — no negative-margin bleed, which would make the
          WRAPPER wider than its container and turn a tab strip that scrolls
          into a page that does. */}
      <div className="overflow-x-auto">
        <div
          role="tablist"
          aria-label="Account sections"
          className="flex min-w-max gap-1 border-b border-brk-border"
        >
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={active === tab.id}
              aria-controls={`panel-${tab.id}`}
              onClick={() => setActive(tab.id)}
              className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                active === tab.id
                  ? "border-brk-accent text-brk-text"
                  : "border-transparent text-brk-dim hover:text-brk-text"
              }`}
            >
              {/* The LABEL and nothing else — no count, because a count is
                  content and the parked ones have none. */}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
      >
        {active === "submissions" && <SubmissionsPanel accountId={accountId} />}
        {active === "documents" && <DocumentsPanel accountId={accountId} />}
        {active === "bonds" && <BondsPanel accountId={accountId} />}
        {TABS.find((t) => t.id === active)?.ready === false && (
          <ComingSoon label={TABS.find((t) => t.id === active)?.label ?? ""} />
        )}
      </div>
    </section>
  );
}

/** The parked tabs. One component, so the three cannot drift into three
 *  different promises. */
function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
      <p className="text-sm font-medium text-brk-text">{label} — coming soon</p>
      {/* Says what it is, and NOT when. A date nobody committed to is the same
          fake as a placeholder row. */}
      <p className="text-sm text-brk-dim">
        We’re still building this. Nothing here yet.
      </p>
    </div>
  );
}

/** This insured's bond orders — the same list, narrowed server-side. */
function SubmissionsPanel({ accountId }: { accountId: string }) {
  const { authedRequest } = useAuth();
  const [rows, setRows] = useState<BondSubmissionListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // `accountId` is additive to the own-scoping, not a replacement for it:
        // PMS still forces the brokerage from the session, so an id belonging
        // to somebody else narrows to nothing rather than reaching it.
        const page = await listBondSubmissions(authedRequest, {
          accountId,
          limit: 25,
        });
        if (!cancelled) setRows(page.items);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-brk-danger">
        {error}
      </p>
    );
  }
  if (rows === null) {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Loading bond submissions…
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
        <p className="text-sm text-brk-text">
          No bond submissions for this insured yet.
        </p>
        <Link
          to="/surety/construction"
          className="text-sm font-medium text-brk-accent hover:underline"
        >
          Start a bond request →
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <BondOrdersTable rows={rows} />
      {/* The same narrowing as a PAGE, so a broker can bookmark or share "this
          insured's bond orders" (BRK-S140). */}
      <Link
        to={`/surety/submissions?accountId=${encodeURIComponent(accountId)}`}
        className="self-start text-sm text-brk-accent hover:underline"
      >
        Open this insured&apos;s orders as a list →
      </Link>
    </div>
  );
}

/**
 * The bonds Signal has ISSUED for this insured (BRK-S175, consuming PMS-S463).
 *
 * DELIBERATELY NOT THE SUBMISSIONS TAB WITH A FILTER. That tab lists bond
 * REQUESTS — drafts included, referrals included, the whole conversation about
 * getting a bond. This one lists the bonds themselves, by the number an obligee
 * quotes back. They sit one tab apart and answer different questions, which is
 * why they are two reads.
 *
 * The EXECUTED FORM downloads through the ACCOUNT's document route, not a route
 * of its own: "one download door for every broker-visible document" (BRK-S146).
 * PMS files the executed PDF as an `account_document`, so the bytes are already
 * behind that door and a second one would be a second set of rules about who may
 * read what.
 */
function BondsPanel({ accountId }: { accountId: string }) {
  const { authedRequest } = useAuth();
  const [bonds, setBonds] = useState<AccountBond[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await listAccountBonds(authedRequest, accountId);
        if (!cancelled) setBonds(page.bonds);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-brk-danger">
        {error}
      </p>
    );
  }
  if (bonds === null) {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Loading bonds…
      </p>
    );
  }
  if (bonds.length === 0) {
    // HONEST rather than encouraging: unlike the Submissions tab, there is no
    // call to action here, because a broker cannot go and issue a bond. The way
    // to one is a request, and that link already lives one tab across.
    return (
      <div className="rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
        <p className="text-sm text-brk-text">
          No bonds issued for this insured yet.
        </p>
      </div>
    );
  }

  // The table is SHARED with the Surety nav's Bonds page (BRK-S181), so the
  // row a broker reads here and there is one set of rendering decisions.
  return <IssuedBondsTable bonds={bonds} accountIdOf={() => accountId} />;
}

/** The account's documents, newest version first, each downloadable. */
function DocumentsPanel({ accountId }: { accountId: string }) {
  const { authedRequest, authedBlob } = useAuth();
  // PMS's own wording for each type (BRK-S146). This panel carried a two-entry
  // label map until now, and it had drifted from the registry it was copying —
  // PMS-S332 deliberately names `final_contract` "Signed contract", where the
  // copy still said "Final contract". So the labels are SERVED, with
  // `documentTypeLabel` keeping a humanised key for a type this bundle has
  // never heard of: a document nobody labelled must still list and download.
  const { labelFor, isSingular } = useDocumentTypes(authedRequest);
  const [documents, setDocuments] = useState<AccountDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await listAccountDocuments(authedRequest, accountId);
        if (!cancelled) setDocuments(page.documents);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId]);

  const download = useCallback(
    async (document: AccountDocument) => {
      setBusyId(document.id);
      setDownloadError(null);
      try {
        // The SERVER names the file; `document.filename` is only the fallback,
        // for a response that carried no `Content-Disposition`.
        saveFile(
          await downloadAccountDocument(authedBlob, accountId, document.id),
          document.filename,
        );
      } catch (err) {
        // A 502 here means the row is live and the object is missing — "retry"
        // and "we have a bug" are different answers, and PMS distinguishes them
        // (PMS-S304), so its sentence is the one to show.
        setDownloadError(messageFor(err));
      } finally {
        setBusyId(null);
      }
    },
    [authedBlob, accountId],
  );

  if (error) {
    return (
      <p role="alert" className="text-sm text-brk-danger">
        {error}
      </p>
    );
  }
  if (documents === null) {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Loading documents…
      </p>
    );
  }
  if (documents.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
        <p className="text-sm text-brk-text">
          No documents on this account yet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {documents.map((doc) => (
          <li
            key={doc.id}
            className="flex flex-col gap-2 rounded-lg border border-brk-border bg-brk-panel p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-brk-text">
                  {labelFor(doc.type)}
                </span>
                <span className="text-xs text-brk-dim">v{doc.version}</span>
                {/* MARKED, never hidden. A document Signal issued is a record
                    of what was issued — a re-approval on new terms does not
                    un-issue the old letter (PMS-S307).

                    Gated on the type being SINGULAR (PMS-S335). `version`
                    counts per (account, type), so `superseded` means two
                    different things: "a newer Program Indication replaced this
                    one", and merely "the broker attached another bank letter
                    afterwards". Marking the first of two bank letters
                    "Superseded" says a true-looking thing that is false —
                    nothing superseded it, and an underwriter reads both. */}
                {doc.superseded && isSingular(doc.type) && (
                  <span className="inline-flex items-center rounded-full bg-brk-bg px-2 py-0.5 text-xs font-medium text-brk-dim">
                    Superseded
                  </span>
                )}
              </span>
              <span className="truncate text-sm text-brk-dim">
                {doc.filename} · {formatListDate(doc.createdAt)}
                {/* WHICH ORDER it came in with, for a submission-scoped row
                    (BRK-S146). Rendered only when PMS actually said — `null` is
                    "no order occasioned this" (the Program Indication letter
                    belongs to the standing program), and an ABSENT key is a PMS
                    that predates PMS-S335. Neither is a reason to print
                    anything, and neither is a reason to drop the row. */}
                {doc.bondSubmissionNumber
                  ? ` · ${doc.bondSubmissionNumber}`
                  : ""}
              </span>
            </div>
            <button
              type="button"
              onClick={() => download(doc)}
              disabled={busyId === doc.id}
              className="shrink-0 self-start rounded-md border border-brk-border px-3 py-1.5 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
            >
              {busyId === doc.id ? "Preparing…" : "Download"}
            </button>
          </li>
        ))}
      </ul>
      {downloadError && (
        <p role="alert" className="text-sm text-brk-danger">
          {downloadError}
        </p>
      )}
    </div>
  );
}
